/**
 * Lazy load Leaflet library to reduce render-blocking resources
 * Only loads when the map is actually needed
 */

let leafletLoaded = false;
let loadPromise: Promise<void> | null = null;

export function loadLeaflet(): Promise<void> {
  // Already loaded
  if (leafletLoaded && window.L) return Promise.resolve();

  // Loading in progress
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve, reject) => {
    // Check if Leaflet is already available (loaded via CDN in head)
    if (window.L) {
      leafletLoaded = true;
      resolve();
      return;
    }

    // Load CSS first (if not already loaded) to avoid FOUC
    if (!document.querySelector('link[href*="leaflet.css"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      link.crossOrigin = '';
      document.head.appendChild(link);
    }

    // Load JS dynamically
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
    script.crossOrigin = '';
    script.async = true;

    script.onload = () => {
      leafletLoaded = true;
      resolve();
    };

    script.onerror = () => {
      loadPromise = null;
      reject(new Error('Failed to load Leaflet'));
    };

    document.body.appendChild(script);
  });

  return loadPromise;
}

/**
 * Check if Leaflet is currently loaded
 */
export function isLeafletLoaded(): boolean {
  return leafletLoaded && !!window.L;
}

// Extend Window interface for TypeScript
declare global {
  interface Window {
    L: typeof import('leaflet');
  }
}
